/*
 * File: ApptModel.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: MST model for an appointment
 */
import {
  types,
  flow,
  getSnapshot,
  applySnapshot,
  getEnv
} from "mobx-state-tree";
import mstIsNumber from "utils/MSTIsNumber";
import Api from "../Api";
import DateFormatter from "utils/DateFormatter";
import ApptFormOptions from "./ApptFormOptions";
import ApptDataModel from "./ApptDataModel";
import { formatISO, addMinutes, parseISO } from "date-fns";

const ApptModel = types
  .model("ApptModel", ApptDataModel)

  .actions(self => {
    return {
      afterCreate() {
        if (self.appointmentId < 1) {
          return;
        }

        self.providerName = [
          self.providerFirstName,
          self.providerLastName
        ].join(" ");
      },
      /**
       * Updates model on input changes
       *
       * @param {string} key
       * @param {*} val
       */
      update(key, val) {
        self[key] = mstIsNumber(self, key, val);
      },

      /**
       * Updates related values on this date object after the appointmentDate changed.
       * @param {Date} date the new appointmentDate value
       */
      updateDateKeys(date) {
        self.update("appointmentTime", date);
        self.update("startTime", date);
        self.update("start", date);
      },

      /**
       * Called whenever the patient selection happens
       * @param {number} patientId
       * @param {string} first
       * @param {string} last
       */
      setPatient(patient) {
        const { patientId, firstName, lastName } = patient;
        if (patientId <= 0) return;
        self.patientId = patientId;
        self.patientFirstName = firstName;
        self.patientLastName = lastName;
      },
      /**
       * Called when a patient is selected to set the correct provider when creating an appt
       * Initially we check to see if a resource is selected and if that resource has a provider
       * If so, we set that resource provider as the appointment provider
       * If not, we set the patient provider as the appointment provider
       */
      setProvider(providerId) {
        self.patientProviderId = providerId;
        //Checks resource, sets resource provider as appt provider (if applicable)
        if (self.resourceId && self.resourceProviderId)
          self.providerID = self.resourceProviderId;
        //If no resource provider, then we set it to the patient provider
        else if (self.resourceId && !self.resourceProviderId)
          self.providerID = providerId;
        //Sets the provider to 'none' if no resource nor patient provider
        else self.providerID = 0;
      },

      /**
       * It revert back the changes.
       */
      cancel(apptStore) {
        if (self.appointmentId > 0) {
          let appt = apptStore.selectedAppt;
          applySnapshot(self, getSnapshot(appt));
        }
      },

      getSaveObj() {
        let saveObj = Object.assign({}, getSnapshot(self));
        saveObj["statusId"] = self.statusId > 1 ? self.statusId : null;
        saveObj["providerId"] = self.providerID > 1 ? self.providerID : null;
        saveObj["typeId"] = self.typeId > 1 ? self.typeId : null;
        saveObj["start"] = formatISO(self.startTime);
        saveObj["end"] = formatISO(addMinutes(self.startTime, self.length));
        return saveObj;
      },

      errorCheck(response) {
        let error = {};
        if (response.appointments) {
          error["appointments"] = response.appointments;
        }
        if (response.transparencies) {
          error["transparencies"] = response.transparencies;
        }
        if (response.confirmAppointmentId) {
          error["confirmAppointmentId"] = response.confirmAppointmentId;
        }
        if (
          response["notScheduleAppointment"] != undefined &&
          response.notScheduleAppointment
        ) {
          error["notScheduleAppointment"] = response.notScheduleAppointment;
        }
        if (Object.keys(error).length === 0) return false;
        self.error = error;
        return true;
      },

      delete: flow(function*() {
        if (self.error && self.error === Object(self.error)) {
          if (self.error.confirmAppointmentId) {
            yield Api.delete(self.error.confirmAppointmentId);
          }
        }
      }),

      confirm: flow(function*() {
        if (self.error && self.error === Object(self.error)) {
          if (self.error.confirmAppointmentId) {
            yield Api.confirm(self.error.confirmAppointmentId);
          }
        }
      }),

      setError(error) {
        self.error = error;
      },

      setNewApptDefaults: flow(function*(preferencesStore) {
        let status = preferencesStore.get(
          "scheduling.appointments.defaultStatus"
        );
        let statuses = yield getEnv(self)
          .storeManager.getStore(ApptFormOptions)
          .getStatuses();
        let findStatus = statuses.find(s => s.name === status);
        self.typeId = preferencesStore.get(
          "scheduling.appointments.defaultType"
        );
        self.statusId = Number(findStatus.id) || 0;
      }),

      save: flow(function*(apptStore) {
        self.error = null;
        if (self.appointmentId > 0) {
          try {
            let res = yield Api.update(self.getSaveObj());
            self.errorCheck(res);
            if (!self.error) apptStore.updateAppointment(getSnapshot(self));
            return res;
          } catch (error) {
            console.log(
              "Error saving appointment to the database from appointment model",
              error
            );
          }
        } else {
          try {
            let res = yield Api.create(self.getSaveObj());
            if (res) {
              self.errorCheck(res);
              if (res.appointmentId) {
                self.appointmentId = res.appointmentId;
              }
              if (!self.error) apptStore.addAppointment(getSnapshot(self));
              return res;
            }
          } catch (error) {
            console.log(
              "Error creating an appointment from appointment model",
              error
            );
          }
        }
      })
    };
  })

  .views(self => ({
    get patient() {
      return {
        patientId: self.patientId,
        patientName: self.patientNameDisplay,
        birthdayString: self.birthday
          ? DateFormatter.birthDate(parseISO(self.birthday))
          : undefined,
        hasAlerts: false
      };
    },

    get patientNameDisplay() {
      return (
        self.patientLastName +
        ", " +
        self.patientFirstName +
        " (" +
        self.displayId +
        ")"
      );
    },

    get providerNameDisplay() {
      return self.providerLastName + ", " + self.providerFirstName;
    },

    get patientName() {
      if (self.patientId <= 0) {
        return "";
      } else {
        return [self.patientFirstName || "", self.patientLastName || ""].join(
          " "
        );
      }
    }
  }));

export default ApptModel;
